import React, { useEffect, useRef, useState } from "react";

import { DeploymentTransferCredentials } from "../../api/deployments";
import { Site } from "../../api/sites";
import SshTerminalPane from "./SshTerminalPane";

export type SshTerminalSession = {
    kind?: "ssh";
    clientId: string;
    title: string;
    historyId: string;
    site: Site;
    credentials: DeploymentTransferCredentials;
    workingDirectory: string;
    siteTerminalId?: number;
};

export type LocalTerminalSession = {
    kind: "local";
    clientId: string;
    title: string;
    workingDirectory: string;
};

export type TerminalSession = SshTerminalSession | LocalTerminalSession;

type Props = {
    sessions: TerminalSession[];
    activeSessionId: string | null;
    sites: Site[];
    selectedSite: Site | null;
    onSelect: (sessionId: string) => void;
    onDuplicate: (sourceSessionId: string) => void;
    onOpenSite: (site: Site) => void;
    onOpenLocal: () => void;
    onClose: (sessionId: string) => void;
    onCloseAll: () => void;
    isNavigatorOpen: boolean;
    onToggleNavigator: () => void;
};

const isSshSession = (session: TerminalSession): session is SshTerminalSession =>
    session.kind !== "local";

const hasSshConnection = (site: Site) =>
    Boolean(site.sshHost?.trim() && site.sshUsername?.trim());

const SshTerminalWorkspace: React.FC<Props> = ({
    sessions,
    activeSessionId,
    sites,
    selectedSite,
    onSelect,
    onDuplicate,
    onOpenSite,
    onOpenLocal,
    onClose,
    onCloseAll,
    isNavigatorOpen,
    onToggleNavigator,
}) => {
    const [isConnectionMenuOpen, setIsConnectionMenuOpen] = useState(false);
    const connectionMenuRef = useRef<HTMLDivElement>(null);
    const activeSession = sessions.find(
        (session) => session.clientId === activeSessionId
    );
    const activeSshSession =
        activeSession && isSshSession(activeSession) ? activeSession : null;
    const sshSites = sites.filter(hasSshConnection);
    const canOpenSelectedSsh = Boolean(selectedSite && hasSshConnection(selectedSite));

    useEffect(() => {
        if (!isConnectionMenuOpen) return;

        const closeOnOutsideClick = (event: MouseEvent) => {
            if (!connectionMenuRef.current?.contains(event.target as Node)) {
                setIsConnectionMenuOpen(false);
            }
        };
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") setIsConnectionMenuOpen(false);
        };

        document.addEventListener("mousedown", closeOnOutsideClick);
        document.addEventListener("keydown", closeOnEscape);
        return () => {
            document.removeEventListener("mousedown", closeOnOutsideClick);
            document.removeEventListener("keydown", closeOnEscape);
        };
    }, [isConnectionMenuOpen]);

    if (sessions.length === 0) return null;

    return (
        <section
            className="dp-ssh-workspace"
            aria-label="터미널 작업 영역"
        >
            <div className="dp-ssh-workspace-bar">
                <button
                    type="button"
                    className={`dp-ssh-workspace-icon dp-ssh-navigator-toggle${
                        isNavigatorOpen ? " is-open" : ""
                    }`}
                    aria-label={isNavigatorOpen ? "배포 경로 접기" : "배포 경로 펼치기"}
                    aria-pressed={isNavigatorOpen}
                    title={isNavigatorOpen ? "배포 경로 접기" : "배포 경로 펼치기"}
                    onClick={onToggleNavigator}
                >
                    <span aria-hidden="true" />
                </button>
                <div className="dp-ssh-tab-strip">
                    <div className="dp-ssh-tab-list" role="tablist" aria-label="터미널 세션">
                        {sessions.map((session) => {
                            const active = session.clientId === activeSessionId;
                            return (
                                <div
                                    key={session.clientId}
                                    className={`dp-ssh-tab${active ? " is-active" : ""}`}
                                >
                                    <button
                                        id={`ssh-tab-${session.clientId}`}
                                        type="button"
                                        role="tab"
                                        aria-selected={active}
                                        aria-controls={`ssh-panel-${session.clientId}`}
                                        title={
                                            isSshSession(session)
                                                ? `${session.site.sshUsername ?? ""}@${session.site.sshHost ?? ""}`
                                                : session.workingDirectory
                                        }
                                        onClick={() => onSelect(session.clientId)}
                                    >
                                        <span
                                            className={isSshSession(session) ? undefined : "is-local"}
                                            aria-hidden="true"
                                        />
                                        <strong>{session.title}</strong>
                                    </button>
                                    <button
                                        type="button"
                                        className="dp-ssh-tab-close"
                                        aria-label={`${session.title} 세션 닫기`}
                                        title="세션 닫기"
                                        onClick={() => onClose(session.clientId)}
                                    >
                                        ×
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    <div className="dp-ssh-new-session" ref={connectionMenuRef}>
                        <button
                            type="button"
                            className="dp-ssh-workspace-icon"
                            aria-label="선택한 서버에 새 SSH 세션"
                            title={
                                canOpenSelectedSsh
                                    ? `${selectedSite?.text}에 새 SSH 세션 열기`
                                    : "SSH 연결 정보가 있는 배포 경로를 선택해 주세요."
                            }
                            disabled={!canOpenSelectedSsh}
                            onClick={() => {
                                if (selectedSite && canOpenSelectedSsh) {
                                    onOpenSite(selectedSite);
                                }
                            }}
                        >
                            +
                        </button>
                        <button
                            type="button"
                            className="dp-ssh-workspace-icon dp-terminal-options-trigger"
                            aria-label="새 터미널 옵션"
                            aria-haspopup="menu"
                            aria-expanded={isConnectionMenuOpen}
                            title="새 터미널 옵션"
                            onClick={() => setIsConnectionMenuOpen((current) => !current)}
                        >
                            <span aria-hidden="true" />
                        </button>
                        {isConnectionMenuOpen && (
                            <div
                                className="dp-ssh-connection-menu"
                                role="menu"
                                aria-label="새 터미널 옵션"
                            >
                                <div className="dp-ssh-connection-menu-title">
                                    새 터미널
                                </div>
                                {activeSshSession && (
                                    <button
                                        type="button"
                                        role="menuitem"
                                        aria-label="현재 SSH 연결 복제"
                                        onClick={() => {
                                            setIsConnectionMenuOpen(false);
                                            onDuplicate(activeSshSession.clientId);
                                        }}
                                    >
                                        <span>
                                            <strong>현재 SSH 연결 복제</strong>
                                            <em>현재 탭</em>
                                        </span>
                                        <small>
                                            {activeSshSession.site.sshUsername}@
                                            {activeSshSession.site.sshHost}:
                                            {activeSshSession.site.sshPort ?? 22}
                                        </small>
                                    </button>
                                )}
                                <button
                                    type="button"
                                    role="menuitem"
                                    aria-label="로컬 PowerShell 열기"
                                    onClick={() => {
                                        setIsConnectionMenuOpen(false);
                                        onOpenLocal();
                                    }}
                                >
                                    <span>
                                        <strong>로컬 PowerShell</strong>
                                    </span>
                                    <small>이 컴퓨터에서 새 터미널 열기</small>
                                </button>
                                {sshSites.length > 0 && (
                                    <div className="dp-ssh-connection-menu-title is-section">
                                        SSH 서버
                                    </div>
                                )}
                                {sshSites.map((site) => (
                                    <button
                                        key={site.id}
                                        type="button"
                                        role="menuitem"
                                        aria-label={`${site.text} SSH 연결`}
                                        onClick={() => {
                                            setIsConnectionMenuOpen(false);
                                            onOpenSite(site);
                                        }}
                                    >
                                        <span>
                                            <strong>{site.text}</strong>
                                            {site.id === selectedSite?.id && <em>선택됨</em>}
                                        </span>
                                        <small>
                                            {site.sshUsername}@{site.sshHost}:{site.sshPort ?? 22}
                                        </small>
                                        <code>{site.remoteUploadPath || site.homePath}</code>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="dp-ssh-workspace-actions">
                    <button
                        type="button"
                        className="dp-ssh-workspace-icon"
                        aria-label="모든 터미널 세션 닫기"
                        title="모든 터미널 세션 닫기"
                        onClick={onCloseAll}
                    >
                        ×
                    </button>
                </div>
            </div>

            <div className="dp-ssh-terminal-stack">
                {sessions.map((session) => {
                    const active = session.clientId === activeSessionId;
                    return (
                        <div
                            key={session.clientId}
                            id={`ssh-panel-${session.clientId}`}
                            className={`dp-ssh-terminal-panel${active ? " is-active" : ""}`}
                            role="tabpanel"
                            aria-labelledby={`ssh-tab-${session.clientId}`}
                            aria-hidden={!active}
                        >
                            <SshTerminalPane
                                kind={isSshSession(session) ? "ssh" : "local"}
                                historyId={isSshSession(session) ? session.historyId : undefined}
                                site={isSshSession(session) ? session.site : undefined}
                                credentials={
                                    isSshSession(session) ? session.credentials : undefined
                                }
                                workingDirectory={session.workingDirectory}
                                active={active}
                                siteTerminalId={
                                    isSshSession(session)
                                        ? session.siteTerminalId
                                        : undefined
                                }
                            />
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

export default SshTerminalWorkspace;
